import React, { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router"
import { useTranslation } from 'react-i18next'
import { Dropdown, DropdownButton } from "react-bootstrap"

import { DELETE, GET, PUT, httpOk } from 'helpers/api'
import { formatDate } from "helpers/helpers"
import { DISCOUNT_TYPE_VALUE, resolveDiscountTypeValue } from 'helpers/discountTypeValues'
import DeleteModal from 'pages/parts/DeleteModal'
import { formatValidPeriod, validPeriodState } from './ValidPeriod'

function getEditPath(item) {
	if (!item?.id) return null
	switch (resolveDiscountTypeValue(item)) {
		// Тип снят с создания: заменён на «Скидка на меню или позиции». Правку оставили для
		// непереведённых старых правил.
		case DISCOUNT_TYPE_VALUE.AUTO_DISH:
			return `/discount/services/update-dish/${item.id}`
		case DISCOUNT_TYPE_VALUE.AUTO_CHEQUE:
			return `/discount/services/update-cheque/${item.id}`
		case DISCOUNT_TYPE_VALUE.MANUAL_CHEQUE:
			return `/discount/services/create-manual/${item.id}`
		case DISCOUNT_TYPE_VALUE.FIRST_ORDER:
			return `/discount/services/update-first-order/${item.id}`
		case DISCOUNT_TYPE_VALUE.MANUAL_DISH:
			return `/discount/services/update-product-manual/${item.id}`
		case DISCOUNT_TYPE_VALUE.MENU_PROMO:
			return `/discount/services/update-menu/${item.id}`
		case DISCOUNT_TYPE_VALUE.BUY_GET:
			return `/discount/services/update-buy-get/${item.id}`
		case DISCOUNT_TYPE_VALUE.GIFT:
			return `/discount/services/update-gift/${item.id}`
		case DISCOUNT_TYPE_VALUE.DELIVERY_DISCOUNT:
			return `/discount/services/update-delivery-discount/${item.id}`
		default:
			return null
	}
}

function Index() {
	const { t } = useTranslation()
	const navigate = useNavigate()

	const [data, setData] = useState([])
	const [systemServices, setSystemServices] = useState([])
	const [modalDelete, setModalDelete] = useState(false)
	const [deleteModalParams, setDeleteModalParams] = useState({ id: 0 })

	async function handleChangeActive(e, type, index, id) {
		var sendDta = {
			"id": id,
			"active": e.target.checked,
		}

		await PUT('/discount/services-active', sendDta, { loader: true })
		getData()
	}

	function goTo(page) {
		navigate(page)
	}

	async function getData() {
		const response = await GET('/discount/services', {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	// Системные скидки (ручная кассира, акции агрегаторов, промокоды) заводит бэкенд сам —
	// правилами они не являются, клиенту можно поменять только название
	async function getSystemServices() {
		const response = await GET('/analytics/discount-services')
		if (httpOk(response)) {
			setSystemServices(response.data
				.filter(item => item.is_system)
				.map(item => ({ ...item, draft_name: item.name })))
		}
	}

	function changeSystemName(id, value) {
		setSystemServices(systemServices.map(item => item.id === id ? { ...item, draft_name: value } : item))
	}

	async function saveSystemName(item) {
		const name = (item.draft_name || '').trim()
		// Пустое или не изменившееся название не отправляем — возвращаем как было
		if (!name || name === item.name) {
			changeSystemName(item.id, item.name)
			return
		}

		const response = await PUT('/discount/services-system-name', { id: item.id, name }, { loader: true })
		if (httpOk(response)) {
			setSystemServices(systemServices.map(service => service.id === item.id ? { ...service, name, draft_name: name } : service))
		} else {
			changeSystemName(item.id, item.name)
		}
	}

	async function deleteItem() {
		const response = await DELETE(`/discount/services/${deleteModalParams.id}`, {}, { loader: true })
		if (httpOk(response)) {
			setModalDelete(false)
			getData()
		}
	}

	useEffect(() => {
		getData()
		getSystemServices()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">
					{t('discount')} [{data.length}]
				</h4>
				<div className="d-flex">
					<DropdownButton title={t('action')}>
						<Dropdown.Item onClick={() => goTo('/discount/services/create-manual')}>
							{t('discount_manual_cheque')}
						</Dropdown.Item>
						<Dropdown.Item onClick={() => goTo('/discount/services/create-product-manual')}>
							{t('discount_manual_dish')}
						</Dropdown.Item>
						<Dropdown.Item onClick={() => goTo('/discount/services/create-cheque')}>
							{t('discount_automatic_cheque')}
						</Dropdown.Item>
						<Dropdown.Item onClick={() => goTo('/discount/services/create-first-order')}>
							{t('discount_automatic_first_order')}
						</Dropdown.Item>
						<Dropdown.Divider />
						<Dropdown.Item onClick={() => goTo('/discount/services/create-menu')}>
							{t('promo_menu_discount')}
						</Dropdown.Item>
						<Dropdown.Item onClick={() => goTo('/discount/services/create-buy-get')}>
							{t('promo_bogo')}
						</Dropdown.Item>
						<Dropdown.Item onClick={() => goTo('/discount/services/create-gift')}>
							{t('promo_gift_dish')}
						</Dropdown.Item>
						<Dropdown.Divider />
						<Dropdown.Item onClick={() => goTo('/discount/services/create-delivery-discount')}>
							{t('discount_delivery')}
						</Dropdown.Item>
					</DropdownButton>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '25%' }}>{t('name2')}</th>
									<th style={{ 'width': '35%' }}>{t('type')}</th>
									<th style={{ 'width': '15%' }} className="text-center">{t('launch_date')}</th>
									<th style={{ 'width': '15%' }} className="text-center">{t('stop_date')}</th>
									<th style={{ 'width': '10%' }} className="text-center text-nowrap">{t('status')}</th>
									<th style={{ 'minWidth': '33px' }}></th>
								</tr>
							</thead>
							<tbody>
								{data?.map((item, index) => {
									const editPath = getEditPath(item)
									return (
										<tr key={item.id ?? index}>
											<td className="text-center">{index + 1}</td>
											<td>
												{editPath
													? <Link to={editPath} className="text-primary cursor">{item.name}</Link>
													: item.name
												}
												{formatValidPeriod(item) &&
													<div className={`small ${validPeriodState(item) ? 'text-danger' : 'text-muted'}`}
														title={validPeriodState(item) === 'future' ? 'Ещё не началась' : validPeriodState(item) === 'expired' ? 'Период закончился' : 'Период действия'}>
														<i className="uil uil-calendar-alt me-1"></i>{formatValidPeriod(item)}
													</div>
												}
											</td>
											<td>{item.service_name}</td>
											<td className="text-center">{formatDate(item.start_at, 'dd.MM.yy HH:mm')}</td>
											<td className="text-center">{formatDate(item.stop_at, 'dd.MM.yy HH:mm')}</td>
											<td>
												<div className="form-check form-switch form-switch-md justify-content-center">
													<input type="checkbox" className="form-check-input" name="active"
														checked={item.active}
														onChange={(e) => handleChangeActive(e, 'checkbox', index, item.id)} />
												</div>
											</td>
											<td>
												<div className="table-dropdown">
													<Dropdown id={`dropdown-discount-${item.id}`}>
														<Dropdown.Toggle variant="outline-primary">
															<i className="uil uil-ellipsis-v"></i>
														</Dropdown.Toggle>
														<Dropdown.Menu>
															{editPath &&
																<Link to={editPath} className="table-dropdown-item">
																	<i className="uil-edit-alt"></i>
																	{t('edit')}
																</Link>
															}
															<div className="table-dropdown-item text-danger"
																onClick={() => {
																	setDeleteModalParams({ id: item.id })
																	setModalDelete(true)
																}}>
																<i className="uil-trash-alt"></i>
																{t('delete')}
															</div>
														</Dropdown.Menu>
													</Dropdown>
												</div>
											</td>
										</tr>
									)
								})}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{systemServices.length > 0 &&
				<div className="card mt-3">
					<div className="card-body">
						<h5 className="mb-1">{t('system_discounts')} [{systemServices.length}]</h5>
						<div className="text-muted mb-3" style={{ fontSize: '13px' }}>{t('system_discounts_hint')}</div>
						<div className="table-responsive">
							<table className="table table-bordered mb-0">
								<thead>
									<tr>
										<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
										<th style={{ 'width': '60%' }}>{t('name2')}</th>
										<th style={{ 'width': '40%' }}>{t('discount_source')}</th>
									</tr>
								</thead>
								<tbody>
									{systemServices.map((item, index) => (
										<tr key={item.id}>
											<td className="text-center">{index + 1}</td>
											<td className="td-input td-input-editable">
												<div className="td-editable">
													<i className="uil uil-pen td-editable-icon"></i>
													<input type="text" className="form-control"
														autoComplete="off"
														maxLength={100}
														value={item.draft_name}
														onChange={(e) => changeSystemName(item.id, e.target.value)}
														onBlur={() => saveSystemName(item)}
														onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur() }} />
												</div>
											</td>
											<td>{t(`discount_source_${item.source}`)}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				</div>
			}

			<DeleteModal
				modalDelete={modalDelete}
				setModalDelete={setModalDelete}
				deleteItem={deleteItem}>
			</DeleteModal>
		</>
	)
}

export default Index
